import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api, { resolveImage, formatApiErrorDetail } from "../lib/api";

export default function GalleryDetail() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [comments, setComments] = useState([]);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot
  const [notFound, setNotFound] = useState(false);

  const loadComments = () =>
    api.get(`/comments/${id}`).then((r) => setComments(r.data));

  useEffect(() => {
    api
      .get(`/gallery/${id}`)
      .then((r) => setItem(r.data))
      .catch(() => setNotFound(true));
    loadComments().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) {
      toast.error("Please add a name and a message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/comments/${id}`, { name: name.trim(), message: message.trim(), website });
      setName("");
      setMessage("");
      await loadComments();
      toast.success("Comment posted.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <div className="px-6 md:px-12 lg:px-20 py-32 text-center">
        <h1 className="font-display font-black text-6xl mb-4">404</h1>
        <p className="font-mono text-sm mb-8">This print doesn't exist (yet).</p>
        <Link to="/gallery" className="font-mono text-xs uppercase tracking-[0.25em] underline">
          Back to gallery
        </Link>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="px-6 md:px-12 lg:px-20 py-32 font-mono text-sm text-[#71717A]">
        Loading…
      </div>
    );
  }

  return (
    <div data-testid="gallery-detail-page" className="px-6 md:px-12 lg:px-20 py-12 md:py-16">
      <Link
        to="/gallery"
        data-testid="back-to-gallery"
        className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-[#71717A] hover:text-black mb-8"
      >
        <ArrowLeft size={14} /> Back to gallery
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        {/* IMAGE - Sticky */}
        <div className="lg:col-span-7">
          <div className="lg:sticky lg:top-28">
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="border border-black/10 bg-white overflow-hidden"
            >
              <img
                src={resolveImage(item.image_url)}
                alt={item.title}
                data-testid="detail-image"
                loading="eager"
                decoding="sync"
                style={{ filter: "contrast(1.04) saturate(1.06)" }}
                className="w-full h-auto object-cover"
              />
            </motion.div>
          </div>
        </div>

        {/* DETAIL + COMMENTS */}
        <div className="lg:col-span-5 space-y-12">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#E6007A] mb-4">
              {item.category}
            </p>
            <h1
              data-testid="detail-title"
              className="font-display font-black text-5xl md:text-6xl leading-[0.9] mb-6"
            >
              {item.title}
            </h1>
            <p className="font-mono text-base leading-relaxed text-[#27272A]">
              {item.description || "A new piece from our DTF press."}
            </p>

            <div className="mt-10 grid grid-cols-2 gap-px bg-black/10 border border-black/10">
              {[
                ["Process", "DTF Transfer"],
                ["Inks", "CMYK + White"],
                ["Cure", "Heat-pressed 165°C"],
                ["Wash", "60+ cycles"],
              ].map(([k, v]) => (
                <div key={k} className="bg-white p-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">
                    {k}
                  </p>
                  <p className="font-display font-black text-base mt-1">{v}</p>
                </div>
              ))}
            </div>
          </div>

          {/* COMMENT FORM */}
          <div className="border-t border-black/15 pt-10">
            <div className="flex items-center gap-3 mb-6">
              <MessageCircle size={18} />
              <h2 className="font-display font-black text-2xl">
                Leave a note ({comments.length})
              </h2>
            </div>
            <form onSubmit={submit} className="space-y-4" data-testid="comment-form">
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                name="website"
              />
              <input
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="comment-name-input"
                className="w-full bg-white border border-black/15 px-4 py-3 font-mono text-sm focus:outline-none focus:border-black focus:ring-2 focus:ring-black/10 rounded-none"
                maxLength={80}
              />
              <textarea
                placeholder="What do you think of this print?"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                data-testid="comment-message-input"
                className="w-full bg-white border border-black/15 px-4 py-3 font-mono text-sm focus:outline-none focus:border-black focus:ring-2 focus:ring-black/10 rounded-none resize-none"
                maxLength={1000}
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="comment-submit-btn"
                className="btn-ink bg-black text-white px-7 py-4 font-mono text-xs uppercase tracking-[0.25em] disabled:opacity-50"
              >
                {submitting ? "Posting…" : "Post comment →"}
              </button>
            </form>
          </div>

          {/* COMMENT LIST */}
          <div className="space-y-4">
            {comments.length === 0 && (
              <p
                data-testid="no-comments"
                className="font-mono text-sm text-[#71717A] py-6 border border-dashed border-black/15 px-5"
              >
                Be the first to comment on this print.
              </p>
            )}
            {comments.map((c) => (
              <div
                key={c.id}
                data-testid={`comment-${c.id}`}
                className="border border-black/10 bg-white p-5"
              >
                <div className="flex items-baseline justify-between gap-4 mb-2">
                  <p className="font-display font-black text-base">{c.name}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
                    {new Date(c.created_at).toLocaleDateString(undefined, {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </p>
                </div>
                <p className="font-mono text-sm leading-relaxed text-[#27272A] whitespace-pre-wrap">
                  {c.message}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
